import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Loader2, AlertTriangle } from "lucide-react";
import type { ManagedUser } from "./types";
import { apiErrorMessage, revokeTrial } from "./trials-api";

type Props = {
  user: ManagedUser | null;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
};

export const ConfirmRevokeDialog = ({
  user,
  open,
  onClose,
  onSuccess,
}: Props) => {
  const [confirmText, setConfirmText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setConfirmText("");
      setSubmitting(false);
    }
  }, [open]);

  if (!user) return null;

  const numericId = parseInt(user.id, 10);
  const canConfirm = confirmText === "REVOKE" && !isNaN(numericId);

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    try {
      await revokeTrial(numericId);
      toast.success(`Trial revoked for ${user.email}`);
      onSuccess();
      onClose();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to revoke trial"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !submitting && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Revoke trial</DialogTitle>
          <DialogDescription>
            Immediately end the trial for {user.name || user.email}.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <div>
              The user will lose access immediately. Their account will be
              locked until the trial is reactivated. This cannot be undone — you
              will need to reactivate to restore access.
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm-revoke">
              Type <span className="font-mono font-semibold">REVOKE</span> to
              confirm
            </Label>
            <Input
              id="confirm-revoke"
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="REVOKE"
              autoComplete="off"
              disabled={submitting}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleConfirm}
            disabled={!canConfirm || submitting}
            className="gap-2"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {submitting ? "Revoking…" : "Revoke trial"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
